import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Copy, Check, MessageCircle, Download, Upload, Search, RotateCcw, ToggleRight,
  BarChart3, ChevronUp, Loader2, Mail, UserSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import RecoveryDialog from './RecoveryDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Demo-grade share card: shareable code, scannable QR, WhatsApp + copy buttons.
 * Each guest list gets its own — code → list → event → promoter mapping is
 * enforced server-side, so two promoters' lists can never merge.
 */
export const ListShareCard = ({ list, token, onUpdate }) => {
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const qrContainerRef = useRef(null);
  const rsvpUrl = `${window.location.origin}/rsvp/${list.code}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(rsvpUrl);
      setCopied(true);
      toast.success('RSVP link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — long-press to select instead');
    }
  };

  const whatsappShare = () => {
    const msg = encodeURIComponent(
      `You're on my list for ${list.event_title || 'Unight'}.\n` +
      `RSVP here (code ${list.code}): ${rsvpUrl}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  /**
   * Rasterise the inline QR SVG to a downloadable PNG. Adds a small label band
   * underneath ("UN-XXXX") so a printed flyer still shows the code if the QR
   * fails to scan.
   */
  const downloadQr = async () => {
    try {
      const svg = qrContainerRef.current?.querySelector('svg');
      if (!svg) {
        toast.error('QR not ready yet — try again');
        return;
      }
      const xml = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = svgUrl;
      });

      // High-DPI canvas — QR at 600px, plus ~80px label band
      const SIZE = 600;
      const LABEL = 90;
      const canvas = document.createElement('canvas');
      canvas.width = SIZE;
      canvas.height = SIZE + LABEL;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      // Label band
      ctx.fillStyle = '#050505';
      ctx.fillRect(0, SIZE, SIZE, LABEL);
      ctx.fillStyle = '#B5FF00';
      ctx.font = 'bold 38px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(list.code, SIZE / 2, SIZE + LABEL / 2);

      URL.revokeObjectURL(svgUrl);

      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = `unight-rsvp-${list.code}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('QR downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Could not download QR');
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Rotate the code? Old code stops working immediately.')) return;
    setRotating(true);
    try {
      const res = await axios.post(
        `${API}/guest-lists/${list.id}/regenerate-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(res.data);
      toast.success(`New code: ${res.data.code}`);
    } catch {
      toast.error('Could not rotate code');
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] border border-zinc-800 p-6 mb-6" data-testid="list-share-card">
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
        {/* QR code */}
        <div ref={qrContainerRef} className="bg-white p-3 inline-block">
          <QRCodeSVG value={rsvpUrl} size={140} level="M" />
        </div>

        {/* Details + actions */}
        <div>
          <div className="font-mono text-xs text-[#B5FF00] tracking-widest mb-1">SHAREABLE CODE</div>
          <div className="font-['Josefin_Sans'] text-4xl text-white tracking-wider mb-3" data-testid="list-share-code">
            {list.code}
          </div>
          <div className="font-mono text-xs text-zinc-500 break-all mb-4">{rsvpUrl}</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copyUrl}
              className="flex items-center gap-2 px-3 py-2 border border-zinc-700 text-white hover:border-[#B5FF00] hover:text-[#B5FF00] text-sm font-mono tracking-wider"
              data-testid="copy-rsvp-link"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'COPIED' : 'COPY LINK'}
            </button>
            <button
              onClick={downloadQr}
              className="flex items-center gap-2 px-3 py-2 border border-zinc-700 text-white hover:border-[#B5FF00] hover:text-[#B5FF00] text-sm font-mono tracking-wider"
              data-testid="download-qr"
            >
              <Download size={14} />
              DOWNLOAD QR
            </button>
            <button
              onClick={whatsappShare}
              className="flex items-center gap-2 px-3 py-2 border border-zinc-700 text-white hover:border-[#25D366] hover:text-[#25D366] text-sm font-mono tracking-wider"
              data-testid="whatsapp-share"
            >
              <MessageCircle size={14} />
              WHATSAPP
            </button>
            <button
              onClick={regenerate}
              disabled={rotating}
              className="flex items-center gap-2 px-3 py-2 border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white text-sm font-mono tracking-wider"
              data-testid="regenerate-code"
            >
              {rotating ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              ROTATE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


/**
 * Live arrivals dashboard: real-time counters + capacity progress bar +
 * sparkline-style hourly histogram. Auto-refreshes every 5s while open.
 */
export const StatsPanel = ({ listId, token, capacity }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/guest-lists/${listId}/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setStats(res.data);
      } catch (err) {
        // Best-effort: stats panel refreshes every 5s, so keep the last good value
        // and don't bubble polling noise to the user.
        if (process.env.NODE_ENV !== 'production') console.debug('[promoter-stats] poll error:', err?.message || err);
      }
    };
    fetchStats();
    const t = setInterval(fetchStats, 5000);
    return () => { mounted = false; clearInterval(t); };
  }, [listId, token]);

  if (!stats) {
    return (
      <div className="bg-[#0A0A0A] border border-zinc-800 p-6 mb-6 flex items-center gap-3">
        <Loader2 size={16} className="animate-spin text-[#B5FF00]" />
        <span className="font-mono text-xs text-zinc-500">LOADING STATS…</span>
      </div>
    );
  }

  const cap = stats.capacity || capacity || 0;
  const fillPct = cap ? Math.min(100, Math.round((stats.confirmed / cap) * 100)) : 0;
  const checkedPct = cap ? Math.min(100, Math.round((stats.checked_in / cap) * 100)) : 0;
  const maxH = Math.max(1, ...stats.arrivals_histogram.map((h) => h.count));

  return (
    <div className="bg-[#0A0A0A] border border-zinc-800 p-6 mb-6" data-testid="stats-panel">
      <div className="grid grid-cols-3 gap-4 mb-5">
        <Counter label="CONFIRMED" value={stats.confirmed} accent />
        <Counter label="CHECKED IN" value={stats.checked_in} />
        <Counter label="WAITLIST" value={stats.waitlist} />
      </div>
      {cap > 0 && (
        <div className="mb-4">
          <div className="flex justify-between font-mono text-xs text-zinc-500 tracking-widest mb-2">
            <span>CAPACITY · {stats.confirmed}/{cap}</span>
            <span className="text-[#B5FF00]">{fillPct}%</span>
          </div>
          <div className="h-2 bg-zinc-900 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#B5FF00]/30" style={{ width: `${fillPct}%` }} />
            <div className="absolute inset-y-0 left-0 bg-[#B5FF00]" style={{ width: `${checkedPct}%` }} />
          </div>
        </div>
      )}
      {stats.arrivals_histogram.length > 0 && (
        <div>
          <div className="font-mono text-xs text-zinc-500 tracking-widest mb-2 flex items-center gap-2">
            <BarChart3 size={12} /> ARRIVALS / HOUR
          </div>
          <div className="flex items-end gap-1 h-16">
            {stats.arrivals_histogram.map((h) => (
              <div key={`hour-${h.hour}`} className="flex-1 bg-[#B5FF00]/70 hover:bg-[#B5FF00] transition-colors"
                   style={{ height: `${(h.count / maxH) * 100}%`, minHeight: '4px' }}
                   title={`${h.hour}: ${h.count}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Counter = ({ label, value, accent = false }) => (
  <div className="text-center">
    <div className={`font-['Josefin_Sans'] text-4xl ${accent ? 'text-[#B5FF00]' : 'text-white'}`}>
      {value}
    </div>
    <div className="font-mono text-xs text-zinc-500 tracking-widest mt-1">{label}</div>
  </div>
);


/**
 * Toolbar above the guests table: search, CSV import, CSV export,
 * and a wait-list-mode toggle.
 */
export const GuestListToolbar = ({ list, token, search, setSearch, onChange, onAfterImport }) => {
  const [importing, setImporting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [resending, setResending] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const fileRef = useRef(null);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/guest-lists/${list.id}/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guestlist_${list.name}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/guest-lists/${list.id}/import-csv`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Imported ${res.data.added} guests · ${res.data.waitlisted} waitlisted · ${res.data.skipped} skipped`);
      onAfterImport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggleWaitlist = async () => {
    setToggling(true);
    try {
      const res = await axios.patch(
        `${API}/guest-lists/${list.id}`,
        { wait_list_mode: !list.wait_list_mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onChange(res.data);
      toast.success(`Wait-list mode ${res.data.wait_list_mode ? 'ON' : 'OFF'}`);
    } catch {
      toast.error('Could not toggle wait-list');
    } finally {
      setToggling(false);
    }
  };

  const resendQrs = async () => {
    if (!window.confirm(
      'Resend the confirmation email (with QR code) to EVERY confirmed guest on this list?\n\n' +
      'Use this if approved guests never received their QR — e.g. earlier email delivery issues.'
    )) return;
    setResending(true);
    try {
      const res = await axios.post(
        `${API}/guest-lists/${list.id}/resend-approved-qrs`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { emails_scheduled, total_confirmed, skipped_no_email } = res.data;
      toast.success(
        `Queued ${emails_scheduled}/${total_confirmed} QR emails` +
        (skipped_no_email ? ` · ${skipped_no_email} skipped (no email)` : '')
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk resend failed');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4" data-testid="guest-toolbar">
      <div className="relative flex-1">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, phone or email"
          className="w-full h-10 bg-[#0A0A0A] border border-zinc-800 text-white pl-9 pr-3 text-sm focus:border-[#B5FF00] focus:outline-none"
          data-testid="guest-search-input"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={toggleWaitlist}
          disabled={toggling}
          className={`flex items-center gap-2 px-3 h-10 border text-sm font-mono tracking-wider ${
            list.wait_list_mode
              ? 'border-[#B5FF00] bg-[#B5FF00]/10 text-[#B5FF00]'
              : 'border-zinc-700 text-zinc-400 hover:text-white'
          }`}
          data-testid="waitlist-toggle"
        >
          <ToggleRight size={14} />
          WAITLIST {list.wait_list_mode ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          className="flex items-center gap-2 px-3 h-10 border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] text-sm font-mono tracking-wider"
          data-testid="import-csv-btn"
        >
          {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          IMPORT CSV
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => importCsv(e.target.files?.[0])}
          data-testid="import-csv-input"
        />
        <button
          onClick={exportCsv}
          className="flex items-center gap-2 px-3 h-10 border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] text-sm font-mono tracking-wider"
          data-testid="export-csv-btn"
        >
          <Download size={14} />
          EXPORT CSV
        </button>
        <button
          onClick={resendQrs}
          disabled={resending}
          className="flex items-center gap-2 px-3 h-10 border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] text-sm font-mono tracking-wider disabled:opacity-50"
          data-testid="resend-qrs-btn"
          title="Email QR codes to all already-confirmed guests"
        >
          {resending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          RESEND QRS
        </button>
        <button
          onClick={() => setRecoveryOpen(true)}
          className="flex items-center gap-2 px-3 h-10 border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] text-sm font-mono tracking-wider"
          data-testid="find-missing-btn"
          title="Find guests who signed up for the event via other channels"
        >
          <UserSearch size={14} />
          FIND MISSING
        </button>
      </div>
      <RecoveryDialog
        open={recoveryOpen}
        onOpenChange={setRecoveryOpen}
        list={list}
        token={token}
        onImported={onAfterImport}
      />
    </div>
  );
};


/** Compact button to promote a wait-listed guest to confirmed. */
export const PromoteButton = ({ guestId, token, onPromoted }) => {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/guests/${guestId}/promote`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Promoted to confirmed');
      onPromoted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not promote');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="p-2 text-zinc-400 hover:text-[#B5FF00]"
      title="Promote to confirmed"
      data-testid={`promote-${guestId}`}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : <ChevronUp size={16} />}
    </button>
  );
};
